import { t } from "@lingui/core/macro"
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { Check, Edit3, GitCompare, History, Loader2, TestTube2, X, XCircle } from "lucide-react"
import { useCallback, useState } from "react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ProjectPickerDialog } from "@/features/config-preview/components/ProjectPickerDialog"
import { useTypedFlag } from "@/features/feature-flags/registry"
import { BandwidthsSection } from "@/features/store-configuration/components/edit/BandwidthsSection"
import { CategoriesSection } from "@/features/store-configuration/components/edit/CategoriesSection"
import { CategorySortOrderSection } from "@/features/store-configuration/components/edit/CategorySortOrderSection"
import { CountryHintsSection } from "@/features/store-configuration/components/edit/CountryHintsSection"
import { GlobalConstraintsSection as GlobalConstraintsSectionExtracted } from "@/features/store-configuration/components/edit/GlobalConstraintsSection"
import { GondolaArrangementsSection } from "@/features/store-configuration/components/edit/GondolaArrangementsSection"
import { GondolaPlacementConfigSection } from "@/features/store-configuration/components/edit/GondolaPlacementConfigSection"
import { LayoutRulesSection } from "@/features/store-configuration/components/edit/LayoutRulesSection"
import { PlacementSequenceSection } from "@/features/store-configuration/components/edit/PlacementSequenceSection"
import { ProhibitedRulesSection } from "@/features/store-configuration/components/edit/ProhibitedRulesSection"
import { WallOrderSection } from "@/features/store-configuration/components/edit/WallOrderSection"
import { SUPPORTED_UI_LOCALES } from "@/features/store-configuration/constants/locales"
import { useComparisonMode } from "@/features/store-configuration/hooks/useComparisonMode"
import {
  getConfigurationVersionFn,
  renameConfigurationVersionFn,
  listConfigurationVersionsFn,
} from "@/features/store-configuration/server/storeConfiguration"
import { usePermissions } from "@/hooks/usePermissions"
import { storeConfigurationQueryKeys } from "@/lib/queryKeys"

const searchSchema = z.object({
  section: z
    .enum([
      "global-constraints",
      "categories",
      "bandwidths",
      "placement-sequence",
      "prohibited-rules",
      "gondola-arrangements",
      "gondola-placement",
      "layout-rules",
      "wall-order",
      "category-sort-order",
      "country-hints",
    ])
    .optional()
    .default("global-constraints"),
})

const configurationVersionQuery = (versionId: string) =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.json(versionId),
    queryFn: () => getConfigurationVersionFn({ data: { id: versionId } }),
  })

const configurationVersionsListQuery = () =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.list({ includeArchived: false }),
    queryFn: () => listConfigurationVersionsFn({ data: { includeArchived: false } }),
  })

export const Route = createFileRoute(
  "/(authenticated)/settings/configuration-versions/$versionId/edit",
)({
  validateSearch: searchSchema,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(configurationVersionQuery(params.versionId))
    void context.queryClient.ensureQueryData(configurationVersionsListQuery())
  },
  component: StoreConfigurationEditPage,
})

function StoreConfigurationEditPage() {
  const { versionId } = Route.useParams()
  const { section } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const { data: config } = useSuspenseQuery(configurationVersionQuery(versionId))
  const { data: versions } = useSuspenseQuery(configurationVersionsListQuery())
  const { hasPermission } = usePermissions()

  // Permission checks
  const canUpdate = hasPermission("configVersion", "update").allowed
  const canViewAuditLog = hasPermission("auditLog", "read").allowed
  const { value: configPreview } = useTypedFlag("config-preview")

  // Preview mode state
  const [showProjectPicker, setShowProjectPicker] = useState(false)

  // Comparison mode state with diff computation
  const {
    comparisonVersionId,
    diff,
    isComparing,
    isLoadingComparison,
    setComparisonVersion,
    clearComparison,
  } = useComparisonMode({ currentData: config })

  // Filter out current version from comparison dropdown
  const otherVersions = versions.filter((v) => v.id !== versionId)

  // Rename editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState("")

  const renameMutation = useMutation({
    mutationFn: (versionLabel: string) =>
      renameConfigurationVersionFn({ data: { id: versionId, versionLabel } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: storeConfigurationQueryKeys.json(versionId),
      })
      setIsEditingName(false)
    },
  })

  const handleStartEditing = () => {
    setEditedName(config?.storeConfiguration.version ?? "")
    setIsEditingName(true)
  }

  const handleCancelEditing = () => {
    setIsEditingName(false)
    setEditedName("")
  }

  const handleSaveName = () => {
    if (editedName.trim() && editedName !== config?.storeConfiguration.version) {
      renameMutation.mutate(editedName.trim())
    } else {
      setIsEditingName(false)
    }
  }

  // Handler for testing on project
  const handleTestOnProject = useCallback(
    (projectId: string) => {
      void navigate({
        to: "/projects/edit/$projectId/optimize",
        params: { projectId },
        search: { previewConfigVersion: versionId },
      })
    },
    [versionId, navigate],
  )

  if (!config) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <XCircle className="mb-4 size-12 text-red-500" />
        <h2 className="text-xl font-semibold">{t`Configuration not found`}</h2>
      </div>
    )
  }

  const storeConfig = config.storeConfiguration
  const globalRules = storeConfig.globalRules as typeof storeConfig.globalRules & {
    merchandiseGroups?: Array<{
      id: string
      code: string
      displayName: string | null
    }>
  }

  // Sections that need a height-constrained parent for scrolling (sticky
  // headers, long tables). Block-layout sections still use `space-y-6`.
  const needsFullHeight =
    section === "placement-sequence" ||
    section === "bandwidths" ||
    section === "gondola-arrangements" ||
    section === "country-hints" ||
    section === "layout-rules"

  return (
    <div className={needsFullHeight ? "flex h-full min-h-0 flex-col" : "space-y-6"}>
      {/* Actions bar */}
      <div
        className={`flex flex-wrap items-center gap-2 ${needsFullHeight ? "shrink-0 pb-6" : ""}`}
      >
        {isEditingName && canUpdate ? (
          <div className="flex items-center gap-2">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="h-10 w-96 font-serif text-2xl font-bold"
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- Intentional UX: focus input when entering edit mode
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName()
                if (e.key === "Escape") handleCancelEditing()
              }}
            />
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              onClick={handleSaveName}
              disabled={renameMutation.isPending}
            >
              {renameMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              onClick={handleCancelEditing}
              disabled={renameMutation.isPending}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          canUpdate && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartEditing}
              title={t`Rename configuration`}
            >
              <Edit3 className="mr-2 size-4" />
              {t`Rename`}
            </Button>
          )
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Compare to dropdown */}
          <div className="flex items-center gap-2">
            <Select value={comparisonVersionId ?? ""} onValueChange={setComparisonVersion}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <GitCompare className="mr-2 size-4 shrink-0" />
                <SelectValue placeholder={t`Compare to...`} />
              </SelectTrigger>
              <SelectContent>
                {otherVersions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    {version.versionLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isComparing && (
              <Button
                variant="outline"
                size="icon"
                onClick={clearComparison}
                title={t`Clear comparison`}
                className="shrink-0"
              >
                <X className="size-4" />
              </Button>
            )}
            {isLoadingComparison && <span className="text-sm text-neutral-500">Loading...</span>}
          </div>
          {configPreview && (
            <Button variant="outline" onClick={() => setShowProjectPicker(true)}>
              <TestTube2 className="mr-2 size-4 shrink-0" />
              <span className="truncate">{t`Test on project...`}</span>
            </Button>
          )}
          {canViewAuditLog && (
            <Link
              to="/settings/audit"
              search={{
                entityType: "store-configuration",
                entityId: versionId,
              }}
            >
              <Button variant="outline" size="sm">
                <History className="mr-2 size-4 shrink-0" />
                <span className="truncate">{t`Audit Log`}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Active section content */}
      {section === "global-constraints" && (
        <GlobalConstraintsSectionExtracted
          globalConstraints={globalRules.globalConstraints}
          versionId={versionId}
          supportedLocales={
            (config.supportedLocales as string[] | undefined) ?? SUPPORTED_UI_LOCALES
          }
          diffData={isComparing ? diff?.globalConstraints : undefined}
          readOnly={!canUpdate}
        />
      )}
      {section === "categories" && (
        <CategoriesSection
          categories={globalRules.categories}
          versionId={versionId}
          diffItems={isComparing ? diff?.categories.items : undefined}
          readOnly={!canUpdate}
        />
      )}
      {section === "bandwidths" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <BandwidthsSection
            bandwidths={
              globalRules.bandwidths as Record<
                string,
                (typeof globalRules.bandwidths)[string] & { id: string }
              >
            }
            versionId={versionId}
            categories={globalRules.categories}
            wallOrderPriority={globalRules.wallOrderPriority}
            diffItems={isComparing ? diff?.bandwidths.items : undefined}
            readOnly={!canUpdate}
          />
        </div>
      )}
      {section === "wall-order" && (
        <WallOrderSection
          wallOrderPriority={globalRules.wallOrderPriority}
          versionId={versionId}
          categories={globalRules.categories}
          diffItems={isComparing ? diff?.wallOrderPriority.items : undefined}
          readOnly={!canUpdate}
        />
      )}
      {section === "category-sort-order" && (
        <CategorySortOrderSection
          categorySortOrder={globalRules.categorySortOrder ?? []}
          allMerchandiseGroupCodes={Object.keys(globalRules.bandwidths)}
          versionId={versionId}
          categories={globalRules.categories}
          readOnly={!canUpdate}
        />
      )}
      {section === "placement-sequence" && (
        <div className="min-h-0 flex-1">
          <PlacementSequenceSection
            versionId={versionId}
            merchandiseGroups={
              (globalRules.merchandiseGroups ?? []) as Array<{
                id: string
                code: string
                displayName: string | null
              }>
            }
            categories={globalRules.categories}
            bandwidths={
              globalRules.bandwidths as Record<
                string,
                (typeof globalRules.bandwidths)[string] & { id: string }
              >
            }
            readOnly={!canUpdate}
          />
        </div>
      )}
      {section === "prohibited-rules" && (
        <ProhibitedRulesSection
          prohibitedRules={
            (globalRules.prohibitedRules ?? []) as Array<
              NonNullable<typeof globalRules.prohibitedRules>[number] & {
                id: string
              }
            >
          }
          versionId={versionId}
          merchandiseGroups={
            (globalRules.merchandiseGroups ?? []) as Array<{
              id: string
              code: string
              displayName: string | null
            }>
          }
          categories={globalRules.categories}
          diffItems={isComparing ? diff?.prohibitedRules.items : undefined}
          readOnly={!canUpdate}
        />
      )}
      {section === "gondola-arrangements" && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <GondolaArrangementsSection
            gondolaArrangements={
              (globalRules.gondolaArrangements ?? []) as Array<
                NonNullable<typeof globalRules.gondolaArrangements>[number] & {
                  id: string
                }
              >
            }
            versionId={versionId}
            categories={globalRules.categories}
            merchandiseGroups={
              (globalRules.merchandiseGroups ?? []) as Array<{
                id: string
                code: string
                displayName: string | null
              }>
            }
            diffItems={isComparing ? diff?.gondolaArrangements.items : undefined}
            readOnly={!canUpdate}
          />
        </div>
      )}
      {section === "gondola-placement" && (
        <GondolaPlacementConfigSection
          gondolaPlacementConfig={
            (globalRules as Record<string, unknown>).gondolaPlacementConfig as
              | import("@/features/shelf-optimization/types/gondola-placement-config").GondolaPlacementConfig
              | undefined
          }
          versionId={versionId}
          readOnly={!canUpdate}
        />
      )}
      {section === "layout-rules" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <LayoutRulesSection
            layoutRules={
              (globalRules as Record<string, unknown>).layoutRules as
                | import("@/features/shelf-optimization/types/layout-rules").LayoutRulesConfig
                | undefined
            }
            versionId={versionId}
            readOnly={!canUpdate}
          />
        </div>
      )}
      {section === "country-hints" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <CountryHintsSection
            countryHints={(config.countryHints as Record<string, string> | undefined) ?? {}}
            versionId={versionId}
            readOnly={!canUpdate}
          />
        </div>
      )}

      {/* Project Picker Dialog for Testing */}
      {configPreview && (
        <ProjectPickerDialog
          open={showProjectPicker}
          onOpenChange={setShowProjectPicker}
          onSelectProject={handleTestOnProject}
        />
      )}
    </div>
  )
}
